import React from 'react';
import { Search, X, SlidersHorizontal, RotateCcw, Bell, FileSpreadsheet, CheckSquare } from 'lucide-react';

const FeeFiltersBar = ({
  searchTerm,
  onSearchChange,
  filters,
  onFilterChange,
  onResetFilters,
  classes = [],
  onOpenAdvancedFilters,
  activeFilterCount = 0,
  selectedStudentIds = [],
  onClearSelection,
  onBulkAlert,
  onBulkExport
}) => {
  const isBulkMode = selectedStudentIds.length > 0;

  if (isBulkMode) {
    return (
      <div
        style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '8px',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '10px',
          boxShadow: '0 1px 2px rgba(59, 130, 246, 0.05)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              background: '#2563eb',
              color: '#ffffff',
              padding: '2px 8px',
              borderRadius: '9999px',
              fontSize: '11px',
              fontWeight: '700',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <CheckSquare size={13} /> {selectedStudentIds.length} Selected
          </span>
          <span style={{ fontSize: '12px', color: '#1e40af', fontWeight: '500' }}>
            Choose an action to apply to all selected students
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-warning"
            onClick={onBulkAlert}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              height: '30px',
              fontSize: '11.5px',
              padding: '0 10px',
              fontWeight: '600',
              background: '#f59e0b',
              borderColor: '#d97706',
              color: '#ffffff'
            }}
          >
            <Bell size={13} /> Send Fee Alert ({selectedStudentIds.length})
          </button>
          <button
            type="button"
            className="btn btn-light"
            onClick={onBulkExport}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              height: '30px',
              fontSize: '11.5px',
              padding: '0 10px',
              fontWeight: '600',
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              color: '#334155'
            }}
          >
            <FileSpreadsheet size={13} /> Export Selected
          </button>
          <button
            type="button"
            className="btn btn-light"
            onClick={onClearSelection}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              height: '30px',
              fontSize: '11.5px',
              padding: '0 10px',
              fontWeight: '600',
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              color: '#64748b'
            }}
          >
            <X size={13} /> Deselect All
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '8px 10px',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '6px',
        marginBottom: '10px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
      }}
    >
      {/* Search Input */}
      <div style={{ flex: '1 1 200px', minWidth: '180px', position: 'relative' }}>
        <Search
          size={14}
          style={{
            position: 'absolute',
            left: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#94a3b8',
            pointerEvents: 'none'
          }}
        />
        <input
          type="text"
          className="form-control"
          placeholder="Search student by name or ID..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{
            height: '30px',
            fontSize: '12px',
            paddingLeft: '28px',
            paddingRight: searchTerm ? '26px' : '8px',
            borderRadius: '6px'
          }}
        />
        {searchTerm && (
          <button
            onClick={() => onSearchChange('')}
            style={{
              position: 'absolute',
              right: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Class Selector */}
      <div style={{ width: '120px' }}>
        <select
          className="form-control"
          value={filters.class || ''}
          onChange={(e) => onFilterChange('class', e.target.value)}
          style={{ height: '30px', fontSize: '12px', padding: '2px 8px', borderRadius: '6px' }}
        >
          <option value="">All Classes</option>
          {classes.map((cls) => (
            <option key={cls} value={cls}>
              {cls}
            </option>
          ))}
        </select>
      </div>

      {/* Section Selector */}
      <div style={{ width: '110px' }}>
        <select
          className="form-control"
          value={filters.section || ''}
          onChange={(e) => onFilterChange('section', e.target.value)}
          style={{ height: '30px', fontSize: '12px', padding: '2px 8px', borderRadius: '6px' }}
        >
          <option value="">All Sections</option>
          {['A', 'B', 'C', 'D'].map((sec) => (
            <option key={sec} value={sec}>
              Section {sec}
            </option>
          ))}
        </select>
      </div>

      {/* Payment Status Selector */}
      <div style={{ width: '130px' }}>
        <select
          className="form-control"
          value={filters.paymentStatus || ''}
          onChange={(e) => onFilterChange('paymentStatus', e.target.value)}
          style={{ height: '30px', fontSize: '12px', padding: '2px 8px', borderRadius: '6px' }}
        >
          <option value="">All Statuses</option>
          <option value="PAID">✓ FULLY PAID</option>
          <option value="PARTIAL">PARTIALLY PAID</option>
          <option value="UNPAID">UNPAID</option>
          <option value="OVERDUE">OVERDUE</option>
        </select>
      </div>

      {/* Due Amount Selector */}
      <div style={{ width: '140px' }}>
        <select
          className="form-control"
          value={filters.dueAmountFilter || ''}
          onChange={(e) => onFilterChange('dueAmountFilter', e.target.value)}
          style={{ height: '30px', fontSize: '12px', padding: '2px 8px', borderRadius: '6px' }}
        >
          <option value="">All Due Amounts</option>
          <option value="FULLY_PAID">✓ Paid (₹0 Due)</option>
          <option value="LESS_2000">Due &lt; ₹2,000</option>
          <option value="LESS_5000">Due &lt; ₹5,000</option>
          <option value="MORE_5000">Due &gt; ₹5,000</option>
          <option value="MORE_10000">Due &gt; ₹10,000</option>
        </select>
      </div>

      {/* More Filters Toggle */}
      <button
        type="button"
        className={`btn ${activeFilterCount > 0 ? 'btn-primary' : 'btn-light'}`}
        onClick={onOpenAdvancedFilters}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          height: '30px',
          fontSize: '11.5px',
          padding: '0 8px',
          fontWeight: '600',
          borderRadius: '6px',
          border: activeFilterCount > 0 ? 'none' : '1px solid #cbd5e1'
        }}
        title="Open Multi-Criteria Filters Drawer"
      >
        <SlidersHorizontal size={13} />
        <span>Filters</span>
        {activeFilterCount > 0 && (
          <span
            style={{
              background: '#ffffff',
              color: '#2563eb',
              borderRadius: '9999px',
              fontSize: '10px',
              fontWeight: '800',
              padding: '0 5px',
              marginLeft: '2px'
            }}
          >
            {activeFilterCount}
          </span>
        )}
      </button>

      {/* Reset Filters */}
      {(activeFilterCount > 0 || searchTerm) && (
        <button
          type="button"
          className="btn btn-light"
          onClick={onResetFilters}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            height: '30px',
            fontSize: '11.5px',
            padding: '0 8px',
            fontWeight: '600',
            borderRadius: '6px',
            border: '1px solid #cbd5e1',
            color: '#64748b'
          }}
          title="Reset all search queries and active filters"
        >
          <RotateCcw size={12} /> Reset
        </button>
      )}
    </div>
  );
};

export default FeeFiltersBar;
